import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FiAward, FiTarget, FiUsers } from 'react-icons/fi';
import '../styles/Pages.css';
import EnhancedHeader from '../components/EnhancedHeader';

const About = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -100]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  return (
    <div className="page-container">
      {/* Enhanced Header with Background Image, Particles & Parallax */}
      <EnhancedHeader
        title="About ACR"
        backgroundImage="/images/residential-care-banner.jpg"
        backgroundGradient="linear-gradient(135deg, rgba(52, 152, 219, 0.7) 0%, rgba(26, 188, 156, 0.7) 100%)"
      />


      {/* Vision Section with Image 1 */}
      <motion.section
        className="section-split"
        style={{ y }}
      >
        <div className="container">
          <div className="split-grid">
            <motion.div
              className="split-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Our Vision</h2>
              <p className="section-text">
                Active Care supported residential accommodational service is a safe, nurturing and structured environment for adults from age 18-65 years. We focus on individuals who want to live independently regardless of their life issues.
              </p>

              <div className="vision-points">
                {[
                  'Safe and nurturing environment',
                  'Structured support services',
                  'Independence promotion',
                  'Comprehensive rehabilitation'
                ].map((point, i) => (
                  <motion.div
                    key={i}
                    className="vision-point"
                    variants={itemVariants}
                    whileHover={{ x: 10 }}
                  >
                    <FiTarget className="point-icon" />
                    <span>{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div className="split-image">
              {/* IMAGE PLACEMENT 1: Vision/Facility Image */}
              <img src="/images/acr13.jpg" alt="Our Vision" />
              
            </div>
          </div>
        </div>
      </motion.section>

      {/* Values Section */}
      <motion.section
        className="values-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Our Core Values</h2>

          <div className="values-grid">
            {[
              {
                icon: FiUsers,
                title: 'Person-Centered',
                desc: 'Focus on individual needs and dignity'
              },
              {
                icon: FiAward,
                title: 'Excellence',
                desc: 'Commitment to highest care standards'
              },
              {
                icon: FiTarget,
                title: 'Empowerment',
                desc: 'Support for achieving life goals'
              }
            ].map((value, i) => (
              <motion.div
                key={i}
                className="value-card"
                variants={itemVariants}
                whileHover={{ y: -15, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)' }}
              >
                <value.icon className="value-icon" />
                <h3>{value.title}</h3>
                <p>{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Quality Standards Section with Image 2 */}
      <motion.section
        className="section-split reverse"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <div className="split-grid">
            <div className="split-image">
              {/* IMAGE PLACEMENT 2: Quality/Standards Image */}
              <img src="/images/acr16.jpg" alt="Quality Standards" />
            </div>

            <motion.div
              className="split-content"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <h2 className="section-title">Quality Standards</h2>

              <p className="section-text">
                We provide outstanding care that meets UK regulatory standards. All staff are trained on safeguarding, behavior management, and trauma informed care.
              </p>

              <div className="standards-list">
                {[
                  'UK Regulatory Compliance',
                  'Professional Staff Training',
                  'Safeguarding Protocols',
                  'Trauma-Informed Care',
                  'Regular Quality Audits',
                  '24/7 Professional Support'
                ].map((standard, i) => (
                  <motion.div
                    key={i}
                    className="standard-item"
                    variants={itemVariants}
                  >
                    <span className="checkmark">✓</span>
                    <span>{standard}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Commitment Section */}
      <motion.section
        className="commitment-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <h2 className="section-title">Our Commitment</h2>

          <motion.div
            className="commitment-box"
            variants={itemVariants}
            whileHover={{
              boxShadow: '0 20px 60px rgba(52, 152, 219, 0.2)',
              transform: 'translateY(-10px)'
            }}
          >
            <p className="commitment-text">
              Within one year of operation we only promise to do what we are going to do. We aim to achieve high life stability and positive outcomes for residents from therapeutic inputs, education, training or employment opportunities.
            </p>

            <div className="commitment-badges">
              {[
                'High Life Stability',
                'Positive Outcomes',
                'Therapeutic Support',
                'Education & Training',
                'Employment Support'
              ].map((badge, i) => (
                <motion.span
                  key={i}
                  className="commitment-badge"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {badge}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default About;
